import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { Loader2, Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';

interface NewsSourceRow {
  id: string;
  name: string;
  feed_url: string;
  trust_score: number;
  indicators: string[] | null;
  is_active: boolean;
  last_sync_at: string | null;
  last_error: string | null;
}

interface NewsSourceMetricRow {
  source_id: string;
  scanned_count: number;
  accepted_count: number;
  rejected_count: number;
  duplicate_count: number;
  error_count: number;
  acceptance_rate: number;
}

export function FishingNewsSourcesPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<NewsSourceRow | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    feed_url: '',
    trust_score: 0.85,
    indicators_csv: '',
    is_active: true,
  });
  const [deleteSource, setDeleteSource] = useState<{ id: string; name: string } | null>(null);

  const sourcesQ = useQuery({
    queryKey: ['admin', 'fishing-news-sources'],
    queryFn: async () => {
      const { data, error } = await dbClient.rpc<NewsSourceRow[]>('admin_fishing_sector_news_sources_list');
      if (error) throw new Error(error.message);
      return normalizeRpcResponse(data);
    },
  });

  const metricsQ = useQuery({
    queryKey: ['admin', 'fishing-news-sources-metrics'],
    queryFn: async () => {
      const { data, error } = await dbClient.rpc<NewsSourceMetricRow[]>('admin_fishing_sector_news_sources_metrics', {
        p_days: 14,
      });
      if (error) throw new Error(error.message);
      return normalizeRpcResponse(data);
    },
  });

  const metricsBySource = useMemo(() => {
    const map = new Map<string, NewsSourceMetricRow>();
    for (const row of metricsQ.data ?? []) map.set(row.source_id, row);
    return map;
  }, [metricsQ.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const indicators = form.indicators_csv
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
      const { error } = await dbClient.rpc('admin_fishing_sector_news_source_upsert', {
        p_source_id: editing?.id ?? null,
        p_name: form.name.trim(),
        p_feed_url: form.feed_url.trim(),
        p_trust_score: Number(form.trust_score),
        p_indicators: indicators,
        p_is_active: form.is_active,
        p_country_code: 'ES',
        p_language_code: 'es',
        p_topic: 'recreativa',
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin', 'fishing-news-sources'] }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'fishing-news-sources-metrics'] }),
      ]);
      setIsDialogOpen(false);
      setEditing(null);
      setForm({ name: '', feed_url: '', trust_score: 0.85, indicators_csv: '', is_active: true });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (sourceId: string) => {
      const { error } = await dbClient.rpc('admin_fishing_sector_news_source_delete', {
        p_source_id: sourceId,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'fishing-news-sources'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fishing-news-sources-metrics'] });
    },
  });

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', feed_url: '', trust_score: 0.85, indicators_csv: '', is_active: true });
    setIsDialogOpen(true);
  };

  const openEdit = (row: NewsSourceRow) => {
    setEditing(row);
    setForm({
      name: row.name,
      feed_url: row.feed_url,
      trust_score: Number(row.trust_score || 0.85),
      indicators_csv: (row.indicators ?? []).join(', '),
      is_active: row.is_active,
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fuentes Noticias Pesca"
        description="Gestión de fuentes editoriales y métricas de calidad (14 días)"
      />

      <div className="flex justify-end">
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={14} />
          Nueva fuente
        </button>
      </div>

      {(sourcesQ.isLoading || metricsQ.isLoading) ? (
        <div className="py-10 flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-3">
          {(sourcesQ.data ?? []).map((row) => {
            const m = metricsBySource.get(row.id);
            return (
              <div key={row.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground truncate">{row.name}</h3>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${row.is_active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-500/15 text-zinc-400'}`}>
                        {row.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground break-all">{row.feed_url}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Trust: <strong>{Number(row.trust_score).toFixed(2)}</strong>
                      {' · '}Indicadores: {(row.indicators ?? []).join(', ') || '—'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      14d: escaneadas {m?.scanned_count ?? 0} · aceptadas {m?.accepted_count ?? 0} ·
                      duplicadas {m?.duplicate_count ?? 0} · rechazadas {m?.rejected_count ?? 0} · errores {m?.error_count ?? 0} ·
                      tasa {m?.acceptance_rate ?? 0}%
                    </p>
                    {row.last_error ? (
                      <p className="text-xs text-red-400 mt-1">Último error: {row.last_error}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => openEdit(row)}
                      className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteSource({ id: row.id, name: row.name })}
                      className="p-2 rounded-md hover:bg-red-500/10 text-muted-foreground hover:text-red-400"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isDialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setIsDialogOpen(false)}>
          <div className="w-full max-w-lg mx-4 rounded-xl border border-border bg-card p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-foreground">{editing ? 'Editar fuente' : 'Nueva fuente'}</h3>

            <div className="space-y-3">
              <input
                type="text"
                placeholder="Nombre"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
              <input
                type="text"
                placeholder="URL RSS"
                value={form.feed_url}
                onChange={(e) => setForm((p) => ({ ...p, feed_url: e.target.value }))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
              <input
                type="number"
                min={0}
                max={1}
                step={0.01}
                placeholder="Trust (0-1)"
                value={form.trust_score}
                onChange={(e) => setForm((p) => ({ ...p, trust_score: Number(e.target.value) }))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
              <input
                type="text"
                placeholder="Indicadores separados por coma"
                value={form.indicators_csv}
                onChange={(e) => setForm((p) => ({ ...p, indicators_csv: e.target.value }))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                  className="rounded border-border"
                />
                Fuente activa
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setIsDialogOpen(false)} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !form.name.trim() || !form.feed_url.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {saveMutation.isPending ? <RefreshCw size={14} className="animate-spin" /> : null}
                Guardar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AdminConfirmDialog
        open={!!deleteSource}
        variant="danger"
        title="Eliminar fuente"
        description={
          deleteSource ? (
            <p>
              ¿Eliminar la fuente <span className="font-medium text-foreground">"{deleteSource.name}"</span>?
            </p>
          ) : null
        }
        confirmLabel="Eliminar"
        onCancel={() => setDeleteSource(null)}
        onConfirm={() => {
          if (deleteSource) {
            deleteMutation.mutate(deleteSource.id);
            setDeleteSource(null);
          }
        }}
        confirmDisabled={deleteMutation.isPending}
      />
    </div>
  );
}
